import { Blog } from "../Model/Blog.model";
import { ServicePage } from "../data/servicePages";

const FALLBACK_SITE_URL = "https://www.subjectbuddy.com";
const FALLBACK_SITE_NAME = "SubjectBuddy";
const FALLBACK_LOGO_URL = `${FALLBACK_SITE_URL}/logos_all/subject-buddy-og-image_1200x630.png`;

function getEnvironmentList(value?: string) {
  return (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export const SEO_CONFIG = {
  siteName: process.env.REACT_APP_NAME || FALLBACK_SITE_NAME,
  author: process.env.REACT_APP_AUTHOR || FALLBACK_SITE_NAME,
  twitterHandle: process.env.REACT_APP_TWITTER_HANDLE || "@SubjectBuddy",
  siteUrl: (process.env.REACT_APP_SITE_URL || FALLBACK_SITE_URL).replace(
    /\/+$/,
    "",
  ),
  defaultTitle: "Academic Mentoring and Tutoring in the UK | SubjectBuddy",
  defaultDescription:
    "SubjectBuddy offers academic mentoring, tutoring, research support, and project guidance for students in the UK.",
  defaultImage: process.env.REACT_APP_DEFAULT_OG_IMAGE || FALLBACK_LOGO_URL,
  defaultImageAlt:
    process.env.REACT_APP_DEFAULT_OG_IMAGE_ALT ||
    "SubjectBuddy logo and website preview",
  locale: "en_GB",
  email: process.env.REACT_APP_EMAIL || "",
  sameAs: getEnvironmentList(process.env.REACT_APP_SOCIAL_LINKS),
};

export function buildCanonicalUrl(path = "/") {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SEO_CONFIG.siteUrl}${normalizedPath}`;
}

export function buildAbsoluteUrl(path?: string) {
  if (!path) {
    return SEO_CONFIG.defaultImage;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return buildCanonicalUrl(path.startsWith("/") ? path : `/${path}`);
}

export function stripHtml(html: string) {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 3).trim()}...`;
}

export function createBlogDescription(blog?: Blog) {
  if (!blog) {
    return "Read practical study guides, research advice, and project mentoring insights from SubjectBuddy.";
  }

  if (blog.description?.trim()) {
    return truncateText(blog.description.trim(), 155);
  }

  const fallbackDescription = blog.tags?.length
    ? `Read SubjectBuddy's guide on ${blog.tags.join(", ")}.`
    : "Read this student support guide from SubjectBuddy.";

  return truncateText(stripHtml(blog.body || "") || fallbackDescription, 155);
}

export function createOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SEO_CONFIG.siteName,
    url: SEO_CONFIG.siteUrl,
    logo: SEO_CONFIG.defaultImage,
    ...(SEO_CONFIG.email ? { email: SEO_CONFIG.email } : {}),
    ...(SEO_CONFIG.sameAs.length ? { sameAs: SEO_CONFIG.sameAs } : {}),
    areaServed: {
      "@type": "Country",
      name: "United Kingdom",
    },
  };
}

export function createWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SEO_CONFIG.siteName,
    url: SEO_CONFIG.siteUrl,
    inLanguage: "en-GB",
  };
}

export function createBreadcrumbSchema(
  items: Array<{ name: string; path: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: buildCanonicalUrl(item.path),
    })),
  };
}

export function createFaqSchema(
  faqs: Array<{ question: string; answer: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function createServiceSchema(service: ServicePage) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.heroTitle,
    description: service.metaDescription,
    areaServed: "United Kingdom",
    serviceType: service.primaryKeyword,
    provider: {
      "@type": "Organization",
      name: SEO_CONFIG.siteName,
      url: SEO_CONFIG.siteUrl,
    },
    url: buildCanonicalUrl(`/services/${service.slug}`),
  };
}
